/**
 * The one place a chart series is assigned a token. `--iw-chart-1..4` are the
 * only ratified categorical colours (CLAUDE.md §4 / design-system.md §5.2) —
 * status tokens (`--iw-success/-warning/-danger/-info`) are reserved for
 * status/delta glyphs and must never be reached for here.
 *
 * **>4-series rule:** colour cycles through the 4 ratified tokens, and a
 * repeat (index >= 4, same colour as an earlier series) is always paired
 * with a different shape/dash so it is never a silent, indistinguishable
 * repeat of an earlier series — never colour-only for anything that can
 * overlap (design-system.md §5.2/§5.3).
 */

export type SeriesShape = 'circle' | 'square' | 'triangle' | 'diamond'

const SHAPES: readonly SeriesShape[] = ['circle', 'square', 'triangle', 'diamond']
/** SVG `stroke-dasharray` values; `undefined` = solid. */
const DASHES: ReadonlyArray<string | undefined> = [undefined, '6 3', '2 3', '8 3 2 3']

export interface SeriesToken {
  /** CSS var reference — inject directly as `fill`/`stroke`, never resolve to a literal hex in a component. */
  color: string
  /** 0-based index into the 4 ratified tokens (for tests / the data-table legend). */
  paletteIndex: number
  /** How many times the 4-token palette has already cycled before this series. */
  cycle: number
  shape: SeriesShape
  /** `undefined` on the first cycle (solid/no differentiation needed — 4 distinct colours is enough). */
  dash: string | undefined
}

export function chartSeriesToken(index: number): SeriesToken {
  const paletteIndex = index % 4
  const cycle = Math.floor(index / 4)
  return {
    color: `var(--iw-chart-${paletteIndex + 1})`,
    paletteIndex,
    cycle,
    shape: SHAPES[cycle % SHAPES.length]!,
    dash: DASHES[cycle % DASHES.length],
  }
}

/** `true` once a dashboard tile has more categorical series than the ratified palette covers. */
export function exceedsRatifiedPalette(seriesCount: number): boolean {
  return seriesCount > 4
}

/**
 * The sequential-ramp CSS var reference (`--iw-chart-seq-1…6`) for `step`
 * of `totalSteps` (IB-291 — the token *contract*; the heatmap/ramp
 * *components* that consume these are separate downstream stories, e.g.
 * View-3 AC3.1). Quantizes an arbitrary step count onto the 6 ratified
 * ramp stops so a caller never has to know there are exactly 6.
 *
 * **Zero-fill semantic** (design doc §2.2, carried from `CreditValueAssembler`
 * AC25): a real lowest/zero value maps to `seq-1` (`step === 0`) — it is
 * still a data point, just the palest one. This helper never represents
 * "no data" — that case is a caller-side pattern/hatch fill
 * (`ChartPatternDefs`), not a ramp step, so a missing value must never be
 * routed through `chartSequentialToken`.
 *
 * @param step 0-based index of the value being coloured.
 * @param totalSteps total number of distinct values in the series (`>= 1`).
 */
export function chartSequentialToken(step: number, totalSteps: number): string {
  const bounded = Math.max(1, totalSteps)
  const clampedStep = Math.min(Math.max(0, step), bounded - 1)
  // Map [0, bounded-1] onto the 6 ratified stops, rounding to the nearest stop.
  const stopIndex = bounded <= 1 ? 0 : Math.round((clampedStep / (bounded - 1)) * 5)
  return `var(--iw-chart-seq-${stopIndex + 1})`
}

/**
 * The diverging-ramp CSS var reference (`--iw-chart-div-neg/-mid/-pos`) for
 * `value` within `[min, max]` (IB-291 — token contract only, per
 * `chartSequentialToken`'s doc). `value === 0` (or `min === max`, a
 * degenerate/all-equal series) always resolves to the neutral midpoint —
 * "true no-signal" (design doc §2.3) — never a derived hue.
 */
export function chartDivergingToken(value: number, min: number, max: number): string {
  if (min === max || value === 0) return 'var(--iw-chart-div-mid)'
  return value > 0 ? 'var(--iw-chart-div-pos)' : 'var(--iw-chart-div-neg)'
}
